import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { OrderingSelectors, OrderingHooks, OrderingConstants } from 'polygon-ordering';
import { DELIVERY_SALE_TYPES } from '../libs/polygon-ordering/src/constants/saleType';
import LocationSummary from './LocationSummary';
import OrderTypeSummary from './OrderTypeSummary';
import TimeSummary from './TimeSummary';
import PaymentMethods from './PaymentMethods';
import getCurrentPaymentMethod from '../selectors/getCurrentPaymentMethod';
import MustSignIn from './MustSignIn';
import PurchaserDetails from './PurchaserDetails';
import { Message } from './OrderMessages';
import { validateFutureMenuItems } from '../thunks/validateFutureMenuItems';
import SectionHeading from './SectionHeading';

const {
  getSaleType,
  getGuestOrderingAvailable,
  getMember,
  getShowMaxValue,
  getMaxOrderValue,
  getBypassDeliveryDetails,
} = OrderingSelectors;
const { useFormattedCurrency } = OrderingHooks;
const { PAYMENT_METHOD, SALE_TYPE } = OrderingConstants;

interface Section {
  title?: string;
  component: React.ComponentType<any>; // Accepts props
  props?: Record<string, any>; // Additional props for specific components
  removeExtraPadding?: boolean;
}

const OrderSummary = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const saleType = useAppSelector(getSaleType);
  const isPickupOrTakeAway =
    saleType === SALE_TYPE.CATERING ||
    saleType === SALE_TYPE.PICKUP ||
    saleType === SALE_TYPE.TAKE_AWAY ||
    saleType === SALE_TYPE.APP_LANE_PICKUP ||
    saleType === SALE_TYPE.WEB_ORDERING;
  const isDineIn = saleType === SALE_TYPE.DINE_IN || saleType === SALE_TYPE.TABLE_ORDER;
  const isDelivery = DELIVERY_SALE_TYPES.includes(saleType);
  const guestOrderingAvailable = useAppSelector(getGuestOrderingAvailable);
  const member = useAppSelector(getMember);

  const mustSignIn = !guestOrderingAvailable && !member;
  const currentPaymentMethod = useAppSelector(getCurrentPaymentMethod);
  const maxOrderValueReached = useAppSelector(getShowMaxValue);
  const maxOrderValueRule = useAppSelector(getMaxOrderValue);

  const disablePickupTimeSelection = useAppSelector(
    state => state.config,
  ).disablePickupTimeSelection;

  const maxOrderValuePrice = useFormattedCurrency({
    cents: maxOrderValueRule?.MaximumOrder,
    hideZeroCents: true,
  });
  const [sections, setSections] = useState<Section[] | null>(null);

  const bypassDeliveryDetails = useAppSelector(getBypassDeliveryDetails);

  useEffect(() => {
    if (isDelivery) {
      dispatch(validateFutureMenuItems());
    }
    getSections();
  }, [mustSignIn, saleType]);

  const getSections = () => {
    const parts: Section[] = [];

    // location
    if (isPickupOrTakeAway || isDineIn || bypassDeliveryDetails) {
      parts.push({
        title: `${t('title.location')}`,
        component: LocationSummary,
      });
    }

    // Order type section
    parts.push({
      title: `${t('title.orderType')}`,
      component: OrderTypeSummary,
    });

    // Time summary
    if (isDelivery || (isPickupOrTakeAway && !disablePickupTimeSelection)) {
      parts.push({
        component: TimeSummary,
      });
    }

    // TODO: Ask Alice when its not in the design for logged in members lol
    // if (!mustSignIn) {
    parts.push({
      title: `${t('title.customerDetails')}`,
      component: PurchaserDetails,
    });
    // }

    if (
      currentPaymentMethod !== PAYMENT_METHOD.FREE.toString() &&
      !mustSignIn &&
      !maxOrderValueReached
    ) {
      parts.push({
        title: t('title.paymentDetails'),
        component: PaymentMethods,
        removeExtraPadding: true,
      });
    }

    setSections(parts);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {sections?.map((section, index) => {
        const Component = section.component;
        return (
          <div key={index}>
            {!!section.title && <SectionHeading title={section.title} />}
            <Component />
          </div>
        );
      })}

      <MustSignIn />
      {maxOrderValueReached && (
        <Message
          showAlertIcon
          message={
            maxOrderValueRule!.Message
              ? maxOrderValueRule!.Message
              : t('maxValueWarning', {
                  amount: `${maxOrderValuePrice}`,
                })
          }
        />
      )}
    </div>
  );
};

export default OrderSummary;
